import { assert, robustFetch } from "@faro-lotv/foundation";
import { IElementDynamicTiledMap } from "@faro-lotv/ielement-types";
import {
  FloorPlanBackgroundTransparency,
  FloorPlanTileMaterial,
} from "@faro-lotv/lotv";
import { createSignedUrlForIElementUri } from "@faro-lotv/project-source";
import {
  DynMapMetadata,
  PointCloudApiClient,
  ProjectApi,
  parseDynMapMetadata,
} from "@faro-lotv/service-wires";
import { Object3D } from "three";
import { DynamicMapTileStore } from "./dynamic-map-tile-store";
import { Rect2 } from "./tile-math";

const CACHE_BUDGET_BYTES = 256 * 1024 * 1024;
const BYTES_PER_PIXEL = 4;

/**
 * The shared, loaded state of a dynamic overview map: metadata, tile cache and material.
 * It is not rendered itself, each view renders it through its own DynamicMapLayer.
 */
export class DynamicMapSource extends Object3D {
  override name = "DynamicMapSource";

  /** Shared by all the tiles, so it can be configured like the material of the other sheets */
  readonly material = new FloorPlanTileMaterial();

  /** World XY bounds of the scan origins, undefined if no scan has a position */
  readonly scanBounds?: Rect2;

  /**
   * @param metadata of the map
   * @param tiles store to load the tiles of this map
   */
  constructor(
    readonly metadata: DynMapMetadata,
    readonly tiles: DynamicMapTileStore,
  ) {
    super();
    this.material.backgroundTransparent = FloorPlanBackgroundTransparency.Alpha;
    this.scanBounds = computeScanBounds(metadata);
  }

  /** Abort all requests and release all the resources */
  dispose(): void {
    this.tiles.dispose();
    this.material.dispose();
  }
}

/** A loaded dynamic map paired with its element */
export type DynamicMapObject = DynamicMapSource & {
  iElement: IElementDynamicTiledMap;
};

/**
 * @returns the XY bounding rectangle of the scan origins, undefined if no scan has a position
 * @param metadata of the map
 */
function computeScanBounds(metadata: DynMapMetadata): Rect2 | undefined {
  const positions = metadata.scans.flatMap((scan) =>
    scan.position ? [scan.position] : [],
  );
  if (!positions.length) return;
  const xs = positions.map((p) => p[0]);
  const ys = positions.map((p) => p[1]);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

type LoadDynamicMapParams = {
  /** To sign the metadata url */
  projectApi: ProjectApi;
  /** To render the tiles */
  pointCloudApiClient?: PointCloudApiClient;
};

/**
 * @returns the loaded dynamic map
 * @param iElement to load
 * @param params dependencies to load the map
 */
export async function loadDynamicMapSource(
  iElement: IElementDynamicTiledMap,
  { projectApi, pointCloudApiClient }: LoadDynamicMapParams,
): Promise<DynamicMapSource> {
  assert(
    pointCloudApiClient,
    "The PointCloud API is required to load a dynamic overview map",
  );

  const signedUrl = createSignedUrlForIElementUri(projectApi, iElement);
  // Sent unchanged with every tile, as the backend caches the metadata by this exact string
  const metadataUrl = await signedUrl.requestSignedUrl();
  const metadata = parseDynMapMetadata(
    await (await robustFetch(metadataUrl)).json(),
  );

  const tiles = new DynamicMapTileStore({
    projectId: projectApi.projectId,
    elementId: iElement.id,
    metadataUrl,
    fetchTile: (request, signal) =>
      pointCloudApiClient.getDynamicMapTile(request, signal),
    refreshMetadataUrl: () => signedUrl.requestSignedUrl(),
    budgetBytes: CACHE_BUDGET_BYTES,
    tileBytes: metadata.tileSizePx ** 2 * BYTES_PER_PIXEL,
  });
  return new DynamicMapSource(metadata, tiles);
}
